import InstallAppCard from "@/src/components/pwa/install-app-card";
import ScButton from "@/src/components/ui-kit/button";
import { PASSWORD_LOGIN_ENABLED } from "@/src/constants/feature-flags";
import { useAuth } from "@/src/contexts/auth.context";
import { useDeleteUserMutation } from "@/src/services/users.blocks.api";
import {
  leaderboardEnabled,
  purgeLeaderboard,
} from "@/src/services/api/leaderboard/leaderboard-functions";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";
import Toast from "react-native-toast-message";
import ConfirmModal from "../../action-modals/confirm-modal";
import { useForgotPassword } from "@/src/hooks/features/auth/use-forgot-password";

const Account = () => {
  const { user, isLoading, logout, isSuperAdmin } = useAuth();
  const { t } = useTranslation();

  // Reuses the Forgot Password request, but from inside an authenticated
  // session: keep the user in the club and report back with a toast instead of
  // redirecting to /login.
  const {
    onSubmit: triggerForgotPassword,
    isSubmitting: isSendingRecoveryEmail,
  } = useForgotPassword({
    redirectToLogin: false,
    successMessage: t("SUCCESS_RESET_PASSWORD_EMAIL_SENT"),
  });

  const [showDelete, setShowDelete] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const { mutate: deleteUser, isPending } = useDeleteUserMutation();

  if (isLoading)
    return (
      <View className="p-6">
        <Text className="text-slate">{t("LABEL_LOADING")}</Text>
      </View>
    );

  if (!user)
    return (
      <View className="p-6">
        <Text className="text-slate">{t("LABEL_NO_USER_DATA")}</Text>
      </View>
    );

  const userEmail = user.email;

  // Handle Change Password
  const handleChangePassword = () => {
    if (!userEmail) return;
    // Both outcomes are reported as toasts by the hook, and the session is left
    // untouched — the reset link arrives by email.
    triggerForgotPassword({ email: userEmail });
  };

  const handleDelete = async () => {
    if (!user?.itemId) return;
    // The leaderboard Function deletes only the caller's own rows, so it runs here
    // (self-deletion), while the session is still active. Best effort: if the
    // deactivation below is refused, the standings are simply recomputed.
    if (leaderboardEnabled) await purgeLeaderboard().catch(() => undefined);
    // Same trigger as the super-admin "Delete User" flow (IdP Deactivate +
    // purgeUserData), scoped to the current user's own id.
    deleteUser(user.itemId, {
      onSuccess: async () => {
        setShowDelete(false);
        await logout();
      },
      onError: () => {
        setShowDelete(false);
        Toast.show({ type: "error", text1: t("ERROR_ACCOUNT_DELETE_FAILED") });
      },
    });
  };

  return (
    <View className="py-4">
      {/* Password & Security — nothing to reset when password login itself is
          off (SSO-only deployments), so hidden alongside it. */}
      {PASSWORD_LOGIN_ENABLED && (
        <View>
          <Text className="font-medium text-lg px-4 pb-4 text-onyx">
            {t("LABEL_PASSWORD_AND_SECURITY")}
          </Text>
          <View className="flex-col p-4 gap-3 border-t items-start border-ash-3">
            {/* `ScButton` runs `text` through `t()` itself, so these are keys, not
                translated strings. Passing `t("BTN_CHANGE_PASSWORD")` translated
                twice — the second lookup got "Change Password" as its key, missed,
                and fell back to echoing it. That reads correctly in English by
                coincidence and breaks in every other language. */}
            <ScButton
              text={
                isSendingRecoveryEmail ? "LABEL_SENDING" : "BTN_CHANGE_PASSWORD"
              }
              onPress={handleChangePassword}
              disabled={isSendingRecoveryEmail}
              loading={isSendingRecoveryEmail}
            />

            <Text className="text-gray-dark">
              {t("HINT_PASSWORD_CHANGE_EMAIL")}
            </Text>
          </View>
        </View>
      )}

      <InstallAppCard />

      {/* Account Deletion */}
      <View>
        <Text className="font-medium text-lg px-4 pb-4 mt-6 text-onyx">
          {t("LABEL_ACCOUNT_DELETION")}
        </Text>
        <View className="flex-col p-4 gap-3 border-t items-start border-ash-3">
          <View
            className={`rounded-[16px] ${isSuperAdmin ? "bg-ash-3" : "bg-red-dark"}`}
          >
            <ScButton
              text="BTN_DELETE_ACCOUNT"
              backgroundColor="transparent"
              onPress={() => setShowDelete(true)}
              disabled={isSuperAdmin}
            />
          </View>
          {isSuperAdmin ? (
            <Text className="text-slate text-sm">
              {t("HINT_SUPER_ADMIN_CANNOT_DELETE_ACCOUNT")}
            </Text>
          ) : (
            <Text className="text-gray-dark">
              {t("HINT_DELETE_ACCOUNT_CANNOT_UNDO")}
            </Text>
          )}
        </View>
      </View>

      {/* Session — the only other way out is an unlabelled ⇅ icon in the
          mobile "More" sheet, which nobody finds. Account settings is where
          people look, so the labelled entry lives here. */}
      <View>
        <Text className="font-medium text-lg px-4 pb-4 mt-6 text-onyx">
          {t("LABEL_SESSION")}
        </Text>
        <View className="flex-col p-4 gap-3 border-t items-start border-ash-3">
          <View className="rounded-[16px] bg-red-dark">
            <ScButton
              text="BTN_LOGOUT"
              backgroundColor="transparent"
              onPress={() => setShowLogout(true)}
            />
          </View>
          <Text className="text-gray-dark">{t("HINT_LOG_OUT")}</Text>
        </View>
      </View>

      <ConfirmModal
        visible={showDelete}
        title="QUEST_DELETE_OWN_ACCOUNT"
        description="HINT_DELETE_OWN_ACCOUNT"
        confirmText="BTN_CONFIRM"
        cancelText="BTN_CANCEL"
        confirmationText={userEmail}
        confirmationLabelKey="HINT_CONFIRMATION_TYPE_EMAIL"
        confirmationLabelParams={{ email: userEmail }}
        onCancel={() => setShowDelete(false)}
        onConfirm={handleDelete}
        loading={isPending}
      />

      {/* Same confirmation as the nav menu, so logging out means the same thing
          from either entry point. `logout()` clears the tokens and replaces the
          route with /login. */}
      <ConfirmModal
        visible={showLogout}
        title="LABEL_LOGOUT_CONFIRM"
        description="HINT_LOGOUT_CONFIRM"
        confirmText="BTN_LOGOUT"
        cancelText="BTN_CANCEL"
        onCancel={() => setShowLogout(false)}
        onConfirm={async () => {
          setShowLogout(false);
          await logout();
        }}
      />
    </View>
  );
};

export default Account;
